import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {TYPES} from "@/modules/archiements/types";
import {AchievementHandlerInterface} from "@/modules/archiements/interfaces";
import {AchievementTypeEnum} from "@/modules/archiements/enums";
import AfterDatabasePreparedEvent from "@/modules/database/events/AfterDatabasePreparedEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {GateTypeEnum} from "@/modules/action-points/enums";

@injectable()
export class GameCreationEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(TYPES.AchievementHandler) private achievementHandler: AchievementHandlerInterface,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterDatabasePreparedEvent, callback: this.onAfterDatabasePreparedEvent},
    ];
  }

  async onAfterDatabasePreparedEvent(event: AfterDatabasePreparedEvent): Promise<void> {
    if (await this.achievementHandler.seeded()) {
      return;
    }

    await this.achievementHandler.create(AchievementTypeEnum.MATCH_WON, 1, {[GateTypeEnum.COINS]: 1});
    await this.achievementHandler.create(AchievementTypeEnum.MATCH_WON, 10, {[GateTypeEnum.COINS]: 3});
    await this.achievementHandler.create(AchievementTypeEnum.MATCH_WON, 50, {[GateTypeEnum.COINS]: 5});
    await this.achievementHandler.create(AchievementTypeEnum.MATCH_WON, 100, {[GateTypeEnum.COINS]: 10});

    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_BUY, 1000, {
      [GateTypeEnum.COINS]: 1,
      [GateTypeEnum.NEGOTIATION]: 10,
      [GateTypeEnum.BALANCE]: 100,
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_BUY, 10000, {
      [GateTypeEnum.COINS]: 3,
      [GateTypeEnum.NEGOTIATION]: 30,
      [GateTypeEnum.BALANCE]: 1000,
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_BUY, 50000, {
      [GateTypeEnum.COINS]: 5,
      [GateTypeEnum.NEGOTIATION]: 50
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_BUY, 1000000, {
      [GateTypeEnum.COINS]: 10,
      [GateTypeEnum.NEGOTIATION]: 100,
      [GateTypeEnum.BALANCE]: 10000,
    });

    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_SELL, 1000, {
      [GateTypeEnum.COINS]: 1,
      [GateTypeEnum.NEGOTIATION]: 10
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_SELL, 10000, {
      [GateTypeEnum.COINS]: 3,
      [GateTypeEnum.NEGOTIATION]: 30
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_SELL, 50000, {
      [GateTypeEnum.COINS]: 5,
      [GateTypeEnum.NEGOTIATION]: 50
    });
    await this.achievementHandler.create(AchievementTypeEnum.PLAYER_SELL, 1000000, {
      [GateTypeEnum.COINS]: 10,
      [GateTypeEnum.NEGOTIATION]: 100
    });

    if (this.configRepository.get('debug')) {
      await this.achievementHandler.increase(AchievementTypeEnum.MATCH_WON, 25);
      await this.achievementHandler.setHighest(AchievementTypeEnum.PLAYER_BUY, 2000);
      await this.achievementHandler.setHighest(AchievementTypeEnum.PLAYER_SELL, 750000);
    }
  }
}